/**
 * Race Calculation Utilities
 * 
 * This file contains all mathematical calculations and data processing
 * functions used for race performance, rankings, and visual effects.
 */

import { Race, RaceAsset } from '../contexts/BettingProvider';
import { AssetPlacementHistory, TugOfWarBar } from '../types';

/**
 * 1. calculateRealTimePerformance
 * 
 * PURPOSE: Calculate how much an asset has gained/lost during the current race
 * 
 * WHAT IT DOES:
 * - Compares current price to the starting price from when betting closed
 * - Returns percentage gain/loss (e.g., +2.5% or -1.3%)
 * 
 * WHEN IT'S USED:
 * - During RACING phase: Shows live performance as race progresses
 * - During RESULTS phase: Shows final performance using exact ending prices
 * 
 * EXAMPLE:
 * Starting price: $100
 * Current price: $102.50
 * Returns: 2.5 (meaning +2.5%)
 * 
 * @param assetSymbol - The crypto symbol (e.g., 'BTC', 'SOL')
 * @param currentRound - Round data with starting/ending prices and phase
 * @param race - Race data with asset information
 * @param currentPrices - Live price feed from WebSocket
 * @returns Percentage gain/loss rounded to 3 decimal places
 */
export const calculateRealTimePerformance = (
  assetSymbol: string,
  currentRound: any,
  race: Race | undefined,
  currentPrices: { [symbol: string]: { price: number; confidence: number; timestamp: number } }
): number => {
  // RESULTS phase: Use exact ending prices for definitive results
  if (currentRound?.phase === 'RESULTS' && 
      currentRound?.startingPrices?.[assetSymbol] && 
      currentRound?.endingPrices?.[assetSymbol]) {
    const startingPrice = currentRound.startingPrices[assetSymbol];
    const endingPrice = currentRound.endingPrices[assetSymbol];
    const percentageGain = ((endingPrice - startingPrice) / startingPrice) * 100;
    return Math.round(percentageGain * 1000) / 1000;
  }

  // Get starting price from when betting closed
  const startingPrice = currentRound?.startingPrices?.[assetSymbol];
  if (!startingPrice || startingPrice === 0) return 0;

  // Get current price: prioritize live WebSocket data, fallback to race data
  let currentPrice = 0;
  if (currentPrices[assetSymbol]) {
    currentPrice = currentPrices[assetSymbol].price;
  } else if (race) {
    const raceAsset = race.assets.find(asset => asset.symbol === assetSymbol);
    if (raceAsset) {
      currentPrice = raceAsset.currentPrice;
    }
  }

  if (!currentPrice || currentPrice === 0) return 0;

  // Calculate percentage change
  const percentageGain = ((currentPrice - startingPrice) / startingPrice) * 100;
  return Math.round(percentageGain * 1000) / 1000;
};

/**
 * 2. calculateSortedAssetsWithPerformance
 * 
 * PURPOSE: Rank all assets by performance to determine race positions (1st, 2nd, etc.)
 * 
 * WHAT IT DOES:
 * - Takes all assets in a race
 * - Calculates each one's real-time performance using calculateRealTimePerformance
 * - Sorts them by performance (best to worst)
 * - Returns sorted array with performance data attached
 * 
 * WHEN IT'S USED:
 * - RaceTrack: To position cars vertically (winner at top)
 * - BetSlip: To show if user's bet is winning
 * - Race displays: To show rankings and medals
 * 
 * WHY IT'S IMPORTANT:
 * - This is the "source of truth" for who's winning the race
 * - Used by multiple components, so needs to be consistent
 * 
 * EXAMPLE:
 * Input: [BTC, SOL] with performances [+1.2%, +2.5%]
 * Output: [SOL (+2.5%), BTC (+1.2%)]  <- SOL is winning!
 * 
 * @param race - The race containing assets to rank
 * @param currentRound - Round data with prices and phase
 * @param currentPrices - Live price feed
 * @returns Array of assets sorted by performance (best first) with realTimePerformance property
 */
export const calculateSortedAssetsWithPerformance = (
  race: Race,
  currentRound: any,
  currentPrices: { [symbol: string]: { price: number; confidence: number; timestamp: number } }
): Array<RaceAsset & { realTimePerformance: number }> => {
  if (!race || !race.assets || race.assets.length === 0) {
    return [];
  }

  return race.assets
    .map(asset => {
      let realTimePerformance = 0;

      // PRIORITY 0: RESULTS phase - use final ending prices for definitive results
      if (currentRound?.phase === 'RESULTS' && 
          currentRound?.startingPrices?.[asset.symbol] && 
          currentRound?.endingPrices?.[asset.symbol]) {
        const startingPrice = currentRound.startingPrices[asset.symbol];
        const endingPrice = currentRound.endingPrices[asset.symbol];
        realTimePerformance = ((endingPrice - startingPrice) / startingPrice) * 100;
        realTimePerformance = Math.round(realTimePerformance * 1000) / 1000;
      }
      // Priority 1: RACING phase - use live price updates
      else if (currentRound?.phase === 'RACING' && currentRound?.startingPrices?.[asset.symbol]) {
        realTimePerformance = calculateRealTimePerformance(asset.symbol, currentRound, race, currentPrices);
      }
      // Priority 2: BETTING phase or fallback - use recent performance data (historical)
      else {
        realTimePerformance = asset.recentPerformance?.[4] || 0;
      }

      return { ...asset, realTimePerformance };
    })
    .sort((a, b) => {
      // Primary sort: by performance (descending - highest first)
      const performanceDiff = b.realTimePerformance - a.realTimePerformance;
      if (performanceDiff !== 0) return performanceDiff;

      // Tie-breaker: alphabetical by symbol (for consistent ordering)
      return a.symbol.localeCompare(b.symbol);
    });
};

/**
 * 3. generatePlacementHistoryFromRaceData
 * 
 * PURPOSE: Generate placement history with medals for each asset in a 1v1 race
 * 
 * WHAT IT DOES:
 * - Takes race winner data from past rounds
 * - For each asset, determines if they won (🥇) or lost (null) in each past race
 * - Calculates betting cost based on recent performance
 * - Detects winning/losing streaks
 * 
 * WHEN IT'S USED:
 * - RaceTrack: To display historical placement medals on each lane
 * 
 * WHY IT'S IMPORTANT:
 * - Shows recent form: "SOL has won 4 out of last 5 races!"
 * - Affects perceived betting value (hot assets = higher cost)
 * - Helps users spot patterns and make informed bets
 * 
 * EXAMPLE:
 * BTC vs SOL history: [SOL, BTC, SOL, SOL, BTC]
 * BTC placement:      [null, 🥇, null, null, 🥇]  <- 2 wins, 3 losses
 * SOL placement:      [🥇, null, 🥇, 🥇, null]    <- 3 wins, 2 losses
 * 
 * @param race - Race with historical winner data
 * @returns Array of placement history for each asset with medals, cost, and streaks
 */
export const generatePlacementHistoryFromRaceData = (race: Race): AssetPlacementHistory[] => {
  if (!race.assets || race.assets.length === 0) return [];
  
  const raceWinnersData = race.assets[0]?.raceWinners || [];
  
  return race.assets.map((asset, index) => {
    const history: Array<1 | 2 | 3 | null> = [];
    
    for (let i = 0; i < Math.min(5, raceWinnersData.length); i++) {
      const raceResult = raceWinnersData[i];
      
      // For 1v1: only show 🥇 if won, otherwise null (lost)
      if (raceResult.first?.symbol === asset.symbol) {
        history.push(1); // Won
      } else {
        history.push(null); // Lost (in 1v1, not winning = losing)
      }
    }
    
    // Pad with nulls if less than 5 races
    while (history.length < 5) {
      history.unshift(null);
    }
    
    // Calculate betting cost based on recent performance
    const firstPlaceCount = history.filter(p => p === 1).length;
    const podiumCount = history.filter(p => p !== null).length;
    const baseCost = 1.0;
    const costMultiplier = baseCost + (firstPlaceCount * 0.15) + (podiumCount * 0.05);
    const cost = Math.min(2.0, Math.max(0.5, costMultiplier));
    
    // Detect streaks (winning or losing patterns)
    let streak = undefined;
    const recentPlacements = [...history].reverse().slice(0, 5);
    const podiumStreak = recentPlacements.findIndex(p => p === null);
    const firstStreak = recentPlacements.findIndex(p => p !== 1);
    
    if (firstStreak >= 2) {
      streak = { type: 'first' as const, count: firstStreak };
    } else if (podiumStreak >= 3) {
      streak = { type: 'podium' as const, count: podiumStreak };
    } else if (recentPlacements.filter(p => p === null).length >= 4) {
      streak = { type: 'cold' as const, count: recentPlacements.filter(p => p === null).length };
    }
    
    return {
      symbol: asset.symbol,
      lane: index + 1,
      placementHistory: history,
      cost,
      streak
    };
  });
};

/**
 * 4. calculateFireIntensity
 * 
 * PURPOSE: Add visual flair to winning history based on HOW impressive the win was
 * 
 * WHAT IT DOES:
 * - Takes an array of past winners with their winning percentages
 * - Ranks them relative to each other
 * - Assigns "fire intensity" levels:
 *   * Level 3 (🔥 Red Fire): Top 25% - most impressive wins
 *   * Level 2 (⚪ Regular): Middle 50% - normal wins
 *   * Level 1 (🔵 Blue Ice): Bottom 25% - weak wins
 * 
 * WHEN IT'S USED:
 * - Race history medals (the gold medals on the track)
 * - Winners ticker (scrolling history at top of race)
 * 
 * WHY IT EXISTS:
 * - Shows pattern recognition: "BTC has been on fire lately!"
 * - Helps users spot hot/cold streaks
 * - Makes the UI more engaging and informative
 * 
 * EXAMPLE:
 * Last 10 wins: [+5%, +2%, +8%, +1%, +3%, +7%, +2%, +4%, +1%, +6%]
 * Sorted:       [+8%, +7%, +6%, +5%, +4%, +3%, +2%, +2%, +1%, +1%]
 * Fire levels:  [ 3,   3,   2,   2,   2,   2,   2,   1,   1,   1 ]
 *                ^Top 25%^  ^-----Middle 50%-----^  ^Bottom 25%^
 * 
 * @param winners - Array of winning results with percentage gains
 * @returns Array of fire intensity levels (1-3) matching input order
 */
export const calculateFireIntensity = (
  winners: Array<{ symbol: string; percentage: number }>
): number[] => {
  // Sort all winners by percentage gain (best to worst)
  const sorted = [...winners].sort((a, b) => b.percentage - a.percentage);

  // Assign intensity based on relative ranking within this group
  return winners.map(winner => {
    // Find this winner's rank in the sorted list
    const rank = sorted.findIndex(
      w => w.symbol === winner.symbol && w.percentage === winner.percentage
    ) + 1;
    const total = sorted.length;

    let intensity;
    if (rank <= Math.ceil(total / 4)) {
      intensity = 3; // Top 25% = 🔥 RED FIRE (exceptional)
    } else if (rank <= Math.ceil(total * 3 / 4)) {
      intensity = 2; // Middle 50% = ⚪ REGULAR (normal)
    } else {
      intensity = 1; // Bottom 25% = 🔵 BLUE ICE (weak)
    }

    return intensity;
  });
};

/**
 * 5. calculateRaceSpecificHistory
 * 
 * PURPOSE: Extract winner history for the scrolling ticker at the top of the race
 * 
 * WHAT IT DOES:
 * - Takes the full race podium history (all past 1st/2nd/3rd place finishes)
 * - Extracts just the 1st place winners (for 1v1 races)
 * - Adds fire intensity to show which wins were most impressive
 * - Formats it for the scrolling ticker display
 * 
 * WHEN IT'S USED:
 * - Race winners ticker (scrolling history at top)
 * - Shows recent matchup results for THIS specific pairing
 * 
 * WHY IT'S IMPORTANT:
 * - Helps users see patterns: "SOL has beaten BTC 7 out of last 10 times"
 * - Fire effects highlight exceptional performances
 * - Provides context for betting decisions
 * 
 * FLOW:
 * 1. Get past race results for this matchup (e.g., BTC vs SOL history)
 * 2. Extract the winner from each past race
 * 3. Calculate fire intensity to show how impressive each win was
 * 4. Return formatted history for ticker display
 * 
 * EXAMPLE OUTPUT:
 * [
 *   { symbol: 'SOL', logo: '◎', color: '🟣', percentage: 2.5, minutesAgo: 1, fireIntensity: 3 },
 *   { symbol: 'BTC', logo: '₿', color: '🟠', percentage: 1.2, minutesAgo: 2, fireIntensity: 2 },
 *   { symbol: 'SOL', logo: '◎', color: '🟣', percentage: 3.1, minutesAgo: 3, fireIntensity: 3 },
 *   ...
 * ]
 * 
 * @param race - Race with historical podium data
 * @param getAssetLogo - Helper to get asset logo emoji
 * @param getAssetColor - Helper to get asset color emoji
 * @returns Formatted history array for ticker display
 */
export const calculateRaceSpecificHistory = (
  race: Race,
  getAssetLogo: (symbol: string) => string,
  getAssetColor: (symbol: string) => string
): Array<{
  symbol: string;
  logo: string;
  color: string;
  percentage: number;
  minutesAgo: number;
  fireIntensity: number;
}> => {
  // In game session mode (1v1), ALWAYS show first place history
  // Old parlay system used selection count, but game sessions are winner-takes-all
  const positionToShow: 'first' | 'second' | 'third' = 'first';

  // Use actual race podium data from completed rounds
  if (race.assets[0]?.raceWinners && race.assets[0].raceWinners.length > 0) {
    const podiumResults = race.assets[0].raceWinners;

    // Extract the specific position from each race result
    const positionFinishers = podiumResults
      .map(podium => podium[positionToShow])
      .filter((finisher): finisher is { symbol: string; percentage: number } => 
        finisher !== null
      );

    if (positionFinishers.length === 0) {
      return [];
    }

    // Calculate fire intensity by comparing finishers WITHIN this position only
    const fireIntensities = calculateFireIntensity(positionFinishers);

    const result = positionFinishers.map((finisher, index) => ({
      symbol: finisher.symbol,
      logo: getAssetLogo(finisher.symbol),
      color: getAssetColor(finisher.symbol),
      percentage: finisher.percentage,
      minutesAgo: index + 1,
      fireIntensity: fireIntensities[index]
    }));

    return result;
  }

  // No race podium data available yet
  return [];
};

/**
 * 6. calculateTugOfWarBars
 * 
 * PURPOSE: Transform matchup history into tug-of-war visualization data
 * 
 * WHAT IT DOES:
 * - Takes raw matchup history from backend (last 10 races)
 * - Calculates dominance percentage for each race (how much winner outperformed)
 * - Normalizes bar widths relative to the most dominant win in the set
 * - Returns array ready for rendering horizontal tug-of-war bars
 * 
 * DOMINANCE CALCULATION:
 * - Both positive: ratio of gains → (winnerGain / loserGain - 1) * 100
 * - Both negative: who fell less → same ratio logic with absolute values
 * - Mixed signs: dominant win → capped at 500% for display
 * 
 * EXAMPLE:
 * Input: [{ BTC: +0.06%, SOL: +0.02%, winner: 'BTC' }, ...]
 * Output: [{ winner: 'BTC', dominancePercent: 200, normalizedWidth: 0.67 }, ...]
 * 
 * @param matchupHistory - Raw history from backend (same format as getMatchupHistory returns)
 * @param assetA - First asset symbol (left side of tug-of-war)
 * @param assetB - Second asset symbol (right side of tug-of-war)
 * @returns Array of TugOfWarBar objects for rendering
 */
export const calculateTugOfWarBars = (
  matchupHistory: any[],
  assetA: string,
  assetB: string
): TugOfWarBar[] => {
  if (!matchupHistory || matchupHistory.length === 0) {
    return [];
  }

  // First pass: calculate dominance for each race
  const barsWithDominance: TugOfWarBar[] = matchupHistory.map((race) => {
    const winner = race.winner;
    const loser = winner === assetA ? assetB : assetA;
    
    const winnerGain = race[winner]?.gain ?? 0;
    const loserGain = race[loser]?.gain ?? 0;
    
    // Calculate dominance percentage
    // We want to show how much better the winner performed relative to the loser
    let dominancePercent: number;
    
    // Handle edge cases
    if (winnerGain === loserGain) {
      // Tie (shouldn't happen with winner declared, but handle it)
      dominancePercent = 0;
    } else if (loserGain === 0) {
      // Loser had exactly 0% - use winner's gain as the margin
      // Scale reasonably (0.1% gain = 100% dominance)
      dominancePercent = Math.round(Math.abs(winnerGain) * 1000);
    } else if ((winnerGain >= 0 && loserGain >= 0) || (winnerGain < 0 && loserGain < 0)) {
      // Both same sign - use ratio
      // For negative gains: winner fell LESS, so ratio of absolutes still works
      const ratio = Math.abs(winnerGain) / Math.abs(loserGain);
      dominancePercent = Math.round((ratio - 1) * 100);
      
      // Handle case where loser actually had larger absolute gain (shouldn't happen)
      if (dominancePercent < 0) {
        dominancePercent = Math.abs(dominancePercent);
      }
    } else {
      // Mixed signs - one positive, one negative = dominant win
      // Calculate as absolute margin scaled similarly to same-sign cases
      // Example: +0.05% vs -0.03% = 0.08% margin → treat like 0.08/0.03 ratio
      const absoluteMargin = Math.abs(winnerGain - loserGain);
      const baseValue = Math.max(Math.abs(winnerGain), Math.abs(loserGain), 0.001);
      dominancePercent = Math.round((absoluteMargin / baseValue) * 100);
    }
    
    // Cap at reasonable max for display (300% is plenty to show dominance)
    dominancePercent = Math.min(300, Math.max(0, dominancePercent));
    
    return {
      winner,
      loser,
      winnerGain,
      loserGain,
      dominancePercent,
      normalizedWidth: 0 // Will be calculated in second pass
    };
  });

  // Second pass: normalize widths relative to max dominance
  const maxDominance = Math.max(...barsWithDominance.map(b => b.dominancePercent), 1);
  
  return barsWithDominance.map(bar => ({
    ...bar,
    // Normalize to 0.15-1.0 range (minimum 15% width so even close races are visible)
    normalizedWidth: 0.15 + (bar.dominancePercent / maxDominance) * 0.85
  }));
};

